/* eslint-disable max-lines-per-function */
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { PaymentParameters, VisitSettlementAdjustCommand } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { mapPaymentMethodToApi, paymentAmountToMoney } from '../shared/form/payment/payment.utils';
import { VisitSettlementFormBean } from '../shared/form/visitSettlementForm.types';
import { medicalServicesToVisitMedicalServiceParameters } from '../shared/medicalServices/medicalServices.utils';
import { useRefetchVisitSettlement } from '../shared/useRefetchVisitSettlement';
import { visitSettlementTranslationKeys } from '../translations';

type VisitSettlementAdjustActionRenderProps = {
   adjust: (formData: VisitSettlementFormBean) => Promise<void>;
};

type VisitSettlementAdjustActionProps = {
   children: (renderProps: VisitSettlementAdjustActionRenderProps) => JSX.Element;
   appointmentId: number;
   onSuccess: VoidFunction;
};

export const VisitSettlementAdjustAction = (props: VisitSettlementAdjustActionProps): JSX.Element => {
   const refetchVisitSettlement = useRefetchVisitSettlement();

   const { executeAsync: execute } = useCqrsCommand<void, VisitSettlementFormBean>(
      (parameters) => {
         const command = new VisitSettlementAdjustCommand();
         command.purchaseId = PurchaseId.forAppointment(AppointmentId.of(props.appointmentId));
         command.medicalServiceParameters = medicalServicesToVisitMedicalServiceParameters(
            parameters.visitSettlement.medicalServices
         );
         command.adjustmentNote = Choice.withUseDefaultIfNull(parameters.adjustment?.note ?? null);
         command.settledCorrectly = Choice.withUseDefaultIfNull(parameters.payment.acceptedCorrectly ?? null);

         if (parameters.payment.amount) {
            const payment: PaymentParameters = {
               paymentAmount: paymentAmountToMoney(parameters.payment.amount),
               paymentMethod: mapPaymentMethodToApi(parameters.payment.method),
               paymentMethodDescription: Choice.withUseDefaultIfNull(parameters.payment.methodDescription ?? null),
            };

            command.payment = Choice.of(payment);
         }
         return command;
      },
      {
         onSuccess: () => {
            refetchVisitSettlement();
            dispatchSnackbarPositiveEvent(visitSettlementTranslationKeys.adjustmentModal.commands.adjust.successfully);
            props.onSuccess();
         },
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(
               visitSettlementTranslationKeys.adjustmentModal.commands.adjust.errors.unexpectedError
            );
         },
      }
   );

   return props.children({
      adjust: execute,
   });
};
